/**
 * Public Relations › Press Release — the wire shapes.
 *
 * Every nullable field is declared `| null` and arrives as `undefined` (the server omits nulls), so
 * compare off the wire with `== null` / `!= null` only.
 */

/** `PressReleaseStatus` — numeric on the wire, mirrored member for member. */
export const PRESS_RELEASE_STATUS = {
  draft: 0,
  published: 1,
} as const;

export type PressReleaseStatus = (typeof PRESS_RELEASE_STATUS)[keyof typeof PRESS_RELEASE_STATUS];

/** One press release. `body` is the sanitised document — on the profile, absent on the list. */
export interface PressReleaseItem {
  id: string;
  title: string;
  /** A CALENDAR date, `YYYY-MM-DD` — `formatDateOnly`, never the instant path. */
  releaseDate: string;
  status: PressReleaseStatus;
  /** The server's label for `status`. */
  statusLabel: string;
  publishedAt?: string | null;
  /** The referenced case's id — ONLY the id; its number and title are read through the case, as the reader. */
  caseId?: string | null;
  body?: string | null;
  attachmentCount: number;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
  isActive: boolean;
}

export interface PressReleaseRequest {
  title: string;
  releaseDate: string;
  body: string;
  /** Null for none. Only a CHANGE is checked server-side; sent back as stored, it needs nothing. */
  caseId: string | null;
}

/** One file attached to a press release. `contentType` is the server's VERIFIED type. */
export interface PressReleaseAttachmentItem {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  hasThumbnail: boolean;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
  isActive: boolean;
}

/** Where the module lives. Every route nests under it. */
export const PRESS_RELEASES_PATH = '/public-relations/press-releases';

export const pressReleasePath = (id: string) => `${PRESS_RELEASES_PATH}/${id}`;

/** The plain-text budget the editor holds the writer to — `PressReleaseFieldRules.BodyTextMaxLength`. */
export const PRESS_RELEASE_BODY_MAX_TEXT = 20_000;

export const PRESS_RELEASE_TITLE_MAX = 300;

/** One file per request, up to 250 MB — `PressReleaseFieldRules.AttachmentMaxBytes`. */
export const PRESS_RELEASE_FILE_MAX_BYTES = 250 * 1024 * 1024;

/** What the server's category accepts. A convenience for the picker; the server verifies the bytes. */
export const PRESS_RELEASE_FILE_ACCEPT = 'application/pdf,.pdf,image/png,.png,image/jpeg,.jpg,.jpeg,video/mp4,.mp4';

/** The same list, as the drop zone states it to the reader. */
export const PRESS_RELEASE_FILE_HINT = 'PDF, PNG, JPEG or MP4';

/** Why a release cannot be changed right now, or null — the server's lock, stated before the click. */
export function pressReleaseLock(release: Pick<PressReleaseItem, 'isActive' | 'status'>): string | null {
  if (!release.isActive) return 'This press release is deleted. Restore it first.';
  if (release.status === PRESS_RELEASE_STATUS.published) return 'This press release is published, so it can no longer be changed.';
  return null;
}

// ── Settings › Channels, and the checklist ────────────────────────────────────────────────

/** One channel a press release can be posted on — the Settings catalog. */
export interface PressReleaseChannelListItem {
  id: string;
  name: string;
  description?: string | null;
  /** Live press releases ticked as posted on it. */
  postedCount: number;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
  isActive: boolean;
}

export interface PressReleaseChannelRequest {
  name: string;
  description: string | null;
}

/** One line of a release's checklist — every live channel, and a deleted one only where ticked. */
export interface PressReleasePostingItem {
  channelId: string;
  channelName: string;
  /** False for a channel deleted since — shown where ticked, never tickable afresh. */
  channelIsActive: boolean;
  isPosted: boolean;
  postedAt?: string | null;
  postedByName?: string | null;
}

/** Public Relations › Settings — a menu, and the catalog pages under it. */
export const PUBLIC_RELATIONS_SETTINGS_PATH = '/public-relations/settings';

export const CHANNELS_PATH = `${PUBLIC_RELATIONS_SETTINGS_PATH}/channels`;

export const channelPath = (id: string) => `${CHANNELS_PATH}/${id}`;

/** Why the checklist cannot be changed right now, or null — the server's rule, stated first. */
export function postingsLock(release: Pick<PressReleaseItem, 'isActive' | 'status'>): string | null {
  if (!release.isActive) return 'This press release is deleted. Restore it first.';
  if (release.status !== PRESS_RELEASE_STATUS.published) return 'Publish this press release before recording where it was posted.';
  return null;
}
